import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { adminGetBlog, adminCreateBlog, adminUpdateBlog, adminDeleteBlog } from "@/lib/adminApi";
import ImagePicker, { resolveImg } from "@/components/admin/ImagePicker";

const EMPTY = { title: "", category: "Rituels", date: "", image: "", excerpt: "", content: [], active: true, order: 0 };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); adminGetBlog().then(setPosts).finally(() => setLoading(false)); };
  useEffect(load, []);

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      const payload = { ...editing, order: parseInt(editing.order) || 0, content: Array.isArray(editing.content) ? editing.content : String(editing.content || "").split("\n\n").filter(Boolean) };
      if (isNew) await adminCreateBlog(payload);
      else await adminUpdateBlog(editing.slug, payload);
      setEditing(null); load();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const remove = async (p) => { if (window.confirm(`Supprimer « ${p.title} » ?`)) { await adminDeleteBlog(p.slug); load(); } };

  return (
    <div data-testid="admin-blog">
      <div className="admin-topbar" style={{ marginBottom: "1.5rem" }}>
        <h1 className="admin-title">Blog — Rituels</h1>
        <button className="adm-btn" onClick={() => { setEditing({ ...EMPTY }); setIsNew(true); setErr(""); }} data-testid="blog-new"><Plus size={15} /> Nouvel article</button>
      </div>

      {loading ? <div className="adm-loader"><div className="spinner" /></div> : (
        <table className="admin-table" data-testid="blog-table">
          <thead><tr><th></th><th>Titre</th><th>Catégorie</th><th>Date</th><th>Statut</th><th></th></tr></thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.slug} data-testid={`blog-row-${p.slug}`}>
                <td><img className="admin-thumb" src={resolveImg(p.image)} alt="" /></td>
                <td><strong style={{ color: "var(--emerald)" }}>{p.title}</strong></td>
                <td>{p.category}</td>
                <td>{p.date}</td>
                <td>{p.active === false ? <span className="pill gray">Masqué</span> : <span className="pill green">Publié</span>}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="icon-btn" onClick={() => { setEditing({ ...EMPTY, ...p }); setIsNew(false); setErr(""); }} data-testid={`blog-edit-${p.slug}`}><Pencil size={15} /></button>
                  <button className="icon-btn danger" onClick={() => remove(p)} data-testid={`blog-delete-${p.slug}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing ? (
        <div className="adm-overlay" onClick={() => setEditing(null)}>
          <div className="adm-drawer" onClick={(e) => e.stopPropagation()} data-testid="blog-drawer">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2>{isNew ? "Nouvel article" : "Modifier l'article"}</h2>
              <button className="icon-btn" onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            {err ? <div className="adm-msg error">{err}</div> : null}
            <div className="adm-form">
              <div className="adm-field"><label>Titre</label><input value={editing.title} onChange={(e) => setField("title", e.target.value)} data-testid="bf-title" /></div>
              <div className="adm-row">
                <div className="adm-field"><label>Catégorie</label><input value={editing.category} onChange={(e) => setField("category", e.target.value)} /></div>
                <div className="adm-field"><label>Date (affichée)</label><input value={editing.date} onChange={(e) => setField("date", e.target.value)} placeholder="12 janvier 2026" /></div>
              </div>
              <ImagePicker label="Image de couverture" value={editing.image} onChange={(v) => setField("image", v)} testid="bf-image" />
              <div className="adm-field"><label>Extrait</label><textarea rows={2} value={editing.excerpt} onChange={(e) => setField("excerpt", e.target.value)} data-testid="bf-excerpt" /></div>
              <div className="adm-field"><label>Contenu (séparez les paragraphes par une ligne vide)</label>
                <textarea rows={8} value={Array.isArray(editing.content) ? editing.content.join("\n\n") : editing.content} onChange={(e) => setField("content", e.target.value.split("\n\n"))} data-testid="bf-content" />
              </div>
              <div className="adm-field" style={{ flexDirection: "row", alignItems: "center", gap: "0.6rem" }}>
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setField("active", e.target.checked)} id="bf-active" style={{ width: "auto" }} />
                <label htmlFor="bf-active" style={{ margin: 0 }}>Article publié</label>
              </div>
            </div>
            <div className="adm-drawer-actions">
              <button className="adm-btn" onClick={save} disabled={saving} data-testid="blog-save">{saving ? "…" : "Enregistrer"}</button>
              <button className="adm-btn ghost" onClick={() => setEditing(null)}>Annuler</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
